import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "eqr_page_zoom";
const MIN = 0.85;
const MAX = 1.35;
const STEP = 0.1;
const DEFAULT = 1;

function clamp(value) {
  return Math.min(MAX, Math.max(MIN, Math.round(value * 100) / 100));
}

function readStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT;
    const n = Number(raw);
    return Number.isFinite(n) ? clamp(n) : DEFAULT;
  } catch {
    return DEFAULT;
  }
}

function applyZoom(scale) {
  document.documentElement.style.setProperty("--eqr-page-zoom", String(scale));
  document.documentElement.style.fontSize = `${16 * scale}px`;
}

/** Page-wide zoom via root font-size (pairs with rem-based UI). */
export function usePageZoom() {
  const [scale, setScale] = useState(DEFAULT);

  useEffect(() => {
    const initial = readStored();
    setScale(initial);
    applyZoom(initial);
  }, []);

  const set = useCallback((next) => {
    const value = clamp(typeof next === "function" ? next(readStored()) : next);
    setScale(value);
    applyZoom(value);
    try {
      localStorage.setItem(STORAGE_KEY, String(value));
    } catch {
      /* ignore */
    }
  }, []);

  const zoomIn = useCallback(() => set((s) => s + STEP), [set]);
  const zoomOut = useCallback(() => set((s) => s - STEP), [set]);
  const reset = useCallback(() => set(DEFAULT), [set]);

  return { scale, zoomIn, zoomOut, reset, canZoomIn: scale < MAX - 0.001, canZoomOut: scale > MIN + 0.001 };
}
